import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Phone, ArrowRight, CheckCircle, ChevronRight, ArrowLeft,
  Clock, Shield, Star, MapPin
} from 'lucide-react';
import { companyInfo, services, cities } from '../../data/mock';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb';
import SEO, { getServiceSeo } from '../seo/SEO';

const ServicePage = () => {
  const { serviceId } = useParams();
  const service = services.find(s => s.id === serviceId);
  const otherServices = services.filter(s => s.id !== serviceId).slice(0, 3);
  const serviceSeo = getServiceSeo(serviceId);

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <SEO 
          title="Service non trouvé | NGL SERVICES"
          description="Page non trouvée"
          noindex={true}
        />
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Service non trouvé</h1>
          <Link to="/" className="text-[hsl(207,78%,42%)] hover:underline">
            Retour à l'accueil
          </Link>
        </div>
      </div>
    );
  }

  // Service Schema.org structured data
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": service.title,
    "description": service.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": companyInfo.name,
      "telephone": companyInfo.phone,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Villiers-sur-Marne",
        "postalCode": "94350",
        "addressCountry": "FR"
      }
    },
    "areaServed": cities.map(city => ({
      "@type": "City",
      "name": city.name
    })),
    "serviceType": service.title
  };

  return (
    <div className="min-h-screen">
      {/* SEO Meta Tags */}
      <SEO 
        title={serviceSeo.title}
        description={serviceSeo.description}
        keywords={serviceSeo.keywords}
        canonical={`/services/${serviceId}`}
        schemaData={serviceSchema}
      />

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${service.image})` }}
        />
        <div className="absolute inset-0 hero-overlay" />
        
        <div className="container mx-auto px-4 relative z-10">
          <Breadcrumb className="mb-6">
            <BreadcrumbList className="text-white/70">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link to="/" className="hover:text-white">Accueil</Link>
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbLink className="text-white/70">Services</BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator className="text-white/50" />
              <BreadcrumbItem>
                <BreadcrumbPage className="text-white">{service.title}</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>

          <div className="max-w-3xl">
            <Badge className="badge-emergency mb-4">
              Intervention rapide
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              {service.title}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {service.description}
            </p>
            <div className="flex flex-wrap gap-4">
              <a href={`tel:${companyInfo.phoneEmergency}`}>
                <Button size="lg" className="emergency-btn rounded-full">
                  <Phone className="w-5 h-5 mr-2" />
                  Urgence : {companyInfo.phoneEmergency}
                </Button>
              </a>
              <Link to="/contact">
                <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full">
                  Demander un devis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <Card className="mb-8 border-0 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
                    Nos prestations {service.title.toLowerCase()}
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.features.map((feature, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-[hsl(210,20%,98%)]">
                        <CheckCircle className="w-5 h-5 text-[hsl(207,78%,42%)] flex-shrink-0 mt-0.5" />
                        <span className="text-[hsl(215,25%,15%)]">{feature}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Why choose us for this service */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-8">
                  <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-6">
                    Pourquoi choisir NGL SERVICES pour vos {service.title.toLowerCase()} ?
                  </h2>
                  <div className="space-y-4">
                    {[
                      { icon: Clock, title: "Intervention rapide", desc: "Déplacement possible le jour même selon disponibilité" },
                      { icon: Shield, title: "Travail garanti", desc: "Garantie sur l'ensemble de nos prestations" },
                      { icon: Star, title: "Expertise", desc: "Techniciens qualifiés et expérimentés" }
                    ].map((item, i) => (
                      <div key={i} className="flex gap-4 p-4 rounded-lg hover:bg-[hsl(210,20%,98%)] transition-colors">
                        <div className="w-12 h-12 rounded-lg bg-[hsl(207,78%,95%)] flex items-center justify-center flex-shrink-0">
                          <item.icon className="w-6 h-6 text-[hsl(207,78%,42%)]" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-[hsl(215,25%,15%)]">{item.title}</h3>
                          <p className="text-muted-foreground text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Card */}
              <Card className="border-0 shadow-lg bg-[hsl(207,78%,42%)] text-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-4">Besoin d'un devis ?</h3>
                  <p className="text-white/80 mb-6">
                    Contactez-nous pour un devis gratuit et sans engagement.
                  </p>
                  <div className="space-y-3">
                    <a href={`tel:${companyInfo.phone}`}>
                      <Button className="w-full bg-white text-[hsl(207,78%,42%)] hover:bg-gray-100 rounded-full">
                        <Phone className="w-4 h-4 mr-2" />
                        {companyInfo.phone}
                      </Button>
                    </a>
                    <Link to="/contact" className="block">
                      <Button className="w-full bg-[hsl(24,95%,53%)] hover:bg-[hsl(24,95%,45%)] rounded-full">
                        Formulaire de contact
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency Card */}
              <Card className="border-0 shadow-lg bg-[hsl(24,95%,53%)] text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-8 h-8" />
                    <div>
                      <p className="font-bold text-lg">Urgences</p>
                      <p className="text-white/80 text-sm">24h/24 – 7j/7</p>
                    </div>
                  </div>
                  <a href={`tel:${companyInfo.phoneEmergency}`}>
                    <Button className="w-full bg-white text-[hsl(24,95%,53%)] hover:bg-gray-100 rounded-full font-bold">
                      <Phone className="w-4 h-4 mr-2" />
                      {companyInfo.phoneEmergency}
                    </Button>
                  </a>
                </CardContent>
              </Card>

              {/* Zones */}
              <Card className="border-0 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-bold text-[hsl(215,25%,15%)] mb-4 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-[hsl(207,78%,42%)]" />
                    Zones d'intervention
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {cities.slice(0, 8).map((city) => (
                      <Link key={city.id} to={`/villes/${city.id}`}>
                        <Badge 
                          variant="outline" 
                          className={`cursor-pointer hover:bg-[hsl(207,78%,95%)] ${
                            city.isPrimary ? 'border-[hsl(207,78%,42%)] text-[hsl(207,78%,42%)]' : ''
                          }`}
                        >
                          {city.name}
                        </Badge>
                      </Link>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="py-16 bg-[hsl(210,20%,98%)]">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-[hsl(215,25%,15%)] mb-8 text-center">
            Découvrez nos autres services
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            {otherServices.map((s) => (
              <Link key={s.id} to={`/services/${s.id}`}>
                <Card className="service-card h-full cursor-pointer border-0 shadow-md">
                  <div className="relative h-40 overflow-hidden rounded-t-lg">
                    <img 
                      src={s.image} 
                      alt={s.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                    <div className="absolute bottom-3 left-4">
                      <h3 className="text-lg font-bold text-white">{s.title}</h3>
                    </div>
                  </div>
                  <CardContent className="p-4">
                    <p className="text-muted-foreground text-sm">{s.shortDesc}</p>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[hsl(215,25%,15%)]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">
            Prêt à démarrer votre projet ?
          </h2>
          <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
            Contactez NGL SERVICES pour obtenir un devis gratuit pour vos travaux de {service.title.toLowerCase()}.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={`tel:${companyInfo.phoneEmergency}`}>
              <Button size="lg" className="emergency-btn rounded-full">
                <Phone className="w-5 h-5 mr-2" />
                {companyInfo.phoneEmergency}
              </Button>
            </a>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[hsl(207,78%,35%)] hover:bg-gray-100 rounded-full">
                Demander un devis gratuit
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServicePage;
